import type { DashboardData } from './types'
import type { Group } from '@/types/database.types'
import { logWarn } from '@/lib/log'

export const CACHE_VERSION = 3
export const CACHE_TTL_MS = 5 * 60 * 1000 // 5 dakika TTL

export interface CacheEnvelope<T> {
  version: number
  timestamp: number
  data: T
}

let _dashboardMemoryCache: DashboardData | null = null
let _groupsMemoryCache: Group[] | null = null

function isEnvelopeValid<T>(envelope: any): envelope is CacheEnvelope<T> {
  if (!envelope || typeof envelope !== 'object') return false
  if (envelope.version !== CACHE_VERSION) return false
  if (typeof envelope.timestamp !== 'number') return false
  if (Date.now() - envelope.timestamp > CACHE_TTL_MS) return false
  return true
}

export function getCachedDashboardData(): DashboardData | null {
  if (_dashboardMemoryCache) return _dashboardMemoryCache

  // EŞDEĞER MUTASYON (bu dosyada altı kez geçiyor): `&&` yerine `||`
  // davranışı DEĞİŞTİRMEZ. Ayrım ancak İKİSİNDEN TAM BİRİ tanımlıysa
  // doğardı; hedeflediğimiz iki ortamda da bu olmuyor — tarayıcı ve
  // Capacitor WebView'da ikisi de var, jest'in `node` ortamında ikisi
  // de yok. Özel gezinti kipi de ayrım yaratmıyor: `localStorage`
  // tanımlı kalıp KULLANIMDA fırlatıyor, onu da alttaki `try` yakalıyor.
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      const persistedStr = localStorage.getItem('snn_dashboard_cache')
      if (!persistedStr) return null

      const envelope = JSON.parse(persistedStr)
      if (isEnvelopeValid<DashboardData>(envelope)) {
        _dashboardMemoryCache = envelope.data
        return _dashboardMemoryCache
      }

      // Legacy or expired cache -> purge!
      localStorage.removeItem('snn_dashboard_cache')
    } catch (e) {
      logWarn('[Abacus] LocalStorage dashboard cache read error:', e)
    }
  }
  return null
}

export function setCachedDashboardData(data: DashboardData): void {
  _dashboardMemoryCache = data
  // EŞDEĞER MUTASYON — gerekçe bu dosyanın ilk aynı korumasında.
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      const envelope: CacheEnvelope<DashboardData> = {
        version: CACHE_VERSION,
        timestamp: Date.now(),
        data,
      }
      localStorage.setItem('snn_dashboard_cache', JSON.stringify(envelope))
    } catch (e) {
      logWarn('[Abacus] LocalStorage dashboard cache write error:', e)
    }
  }
}

export function clearDashboardCache(): void {
  _dashboardMemoryCache = null
  // EŞDEĞER MUTASYON — gerekçe bu dosyanın ilk aynı korumasında.
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem('snn_dashboard_cache')
    } catch (_e) {
      // ignore
    }
  }
}

export function getCachedGroups(): Group[] | null {
  if (_groupsMemoryCache) return _groupsMemoryCache

  // EŞDEĞER MUTASYON — gerekçe bu dosyanın ilk aynı korumasında.
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      const persistedStr = localStorage.getItem('snn_groups_cache')
      if (!persistedStr) return null

      const envelope = JSON.parse(persistedStr)
      if (isEnvelopeValid<Group[]>(envelope)) {
        _groupsMemoryCache = envelope.data
        return _groupsMemoryCache
      }

      localStorage.removeItem('snn_groups_cache')
    } catch (e) {
      logWarn('[Abacus] LocalStorage groups cache read error:', e)
    }
  }
  return null
}

export function setCachedGroups(groups: Group[]): void {
  _groupsMemoryCache = groups
  // EŞDEĞER MUTASYON — gerekçe bu dosyanın ilk aynı korumasında.
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      const envelope: CacheEnvelope<Group[]> = {
        version: CACHE_VERSION,
        timestamp: Date.now(),
        data: groups,
      }
      localStorage.setItem('snn_groups_cache', JSON.stringify(envelope))
    } catch (e) {
      logWarn('[Abacus] LocalStorage groups cache write error:', e)
    }
  }
}

export function clearGroupsCache(): void {
  _groupsMemoryCache = null
  // EŞDEĞER MUTASYON — gerekçe bu dosyanın ilk aynı korumasında.
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem('snn_groups_cache')
    } catch (_e) {
      // ignore
    }
  }
}
